import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid } from "recharts";
import { cn } from "@/lib/utils";
import {
  Key,
  Activity,
  Webhook,
  Zap,
  Clock,
  AlertCircle,
  TrendingUp,
  BarChart3,
} from "lucide-react";

// ==================== TYPES ====================

interface PartnerStats {
  active_keys: number;
  total_keys: number;
  total_requests: number;
  active_webhooks: number;
  total_webhooks: number;
  delivered_events: number;
  failed_events: number;
  delivery_rate: number;
}

interface DailyUsage {
  date: string;
  count: number;
  errors: number;
}

interface TopEndpoint {
  path: string;
  method: string;
  count: number;
}

interface PerKeyUsage {
  key_id: string;
  key_name: string;
  key_prefix: string;
  request_count: number;
}

interface UsageData {
  daily_requests: DailyUsage[];
  top_endpoints: TopEndpoint[];
  avg_latency_ms: number;
  total_requests: number;
  error_rate: number;
  per_key: PerKeyUsage[];
}

interface WebhookEvent {
  id: string;
  webhook_id: string;
  webhook_url: string;
  event_type: string;
  status: "delivered" | "failed" | "pending";
  attempts: number;
  max_attempts: number;
  last_attempt_at: string | null;
  last_response_code: number | null;
  delivered_at: string | null;
  created_at: string;
}

interface WebhookEventsResponse {
  events: WebhookEvent[];
  total: number;
}

// ==================== HELPERS ====================

function formatNumber(num: number): string {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num.toLocaleString();
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatDateTime(dateStr: string | null) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getMethodBadge(method: string) {
  const colors: Record<string, string> = {
    GET: "bg-blue-500/10 text-blue-600",
    POST: "bg-green-500/10 text-green-600",
    PATCH: "bg-amber-500/10 text-amber-600",
    DELETE: "bg-red-500/10 text-red-600",
  };
  return <Badge className={cn("border-0 text-xs font-mono", colors[method] || "")}>{method}</Badge>;
}

function getEventStatusBadge(status: string) {
  switch (status) {
    case "delivered":
      return <Badge className="bg-green-500/10 text-green-600 border-0">Delivered</Badge>;
    case "failed":
      return <Badge variant="destructive" className="border-0">Failed</Badge>;
    case "pending":
      return <Badge variant="secondary">Pending</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

// ==================== CHART CONFIG ====================

const chartConfig = {
  count: {
    label: "Requests",
    color: "hsl(var(--chart-1))",
  },
  errors: {
    label: "Errors",
    color: "hsl(0, 84%, 60%)",
  },
};

// ==================== COMPONENT ====================

export default function UsageDashboardPage() {
  // ==================== QUERIES ====================

  const { data: stats, isLoading: statsLoading } = useQuery<PartnerStats>({
    queryKey: ["partner-api-stats"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/admin/partner-api/stats");
      return response.json();
    },
  });

  const { data: usage, isLoading: usageLoading } = useQuery<UsageData>({
    queryKey: ["partner-api-usage"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/admin/partner-api/usage?days=30");
      return response.json();
    },
  });

  const { data: webhookEvents, isLoading: eventsLoading } = useQuery<WebhookEventsResponse>({
    queryKey: ["partner-webhook-events"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/admin/partner-api/webhook-events?limit=20");
      return response.json();
    },
  });

  // ==================== KPI CARDS ====================

  const kpis = [
    {
      title: "Active Keys",
      value: stats?.active_keys ?? 0,
      subtitle: `${stats?.total_keys ?? 0} total`,
      icon: Key,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
    },
    {
      title: "Total Requests",
      value: formatNumber(stats?.total_requests ?? 0),
      subtitle: `${usage?.error_rate?.toFixed(1) ?? 0}% error rate`,
      icon: Activity,
      color: "text-green-500",
      bgColor: "bg-green-500/10",
    },
    {
      title: "Avg Latency",
      value: `${usage?.avg_latency_ms ?? 0}ms`,
      subtitle: "Response time",
      icon: Clock,
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
    },
    {
      title: "Active Webhooks",
      value: stats?.active_webhooks ?? 0,
      subtitle: `${stats?.total_webhooks ?? 0} total`,
      icon: Webhook,
      color: "text-amber-500",
      bgColor: "bg-amber-500/10",
    },
    {
      title: "Delivery Rate",
      value: `${stats?.delivery_rate?.toFixed(1) ?? 0}%`,
      subtitle: `${formatNumber(stats?.delivered_events ?? 0)} delivered`,
      icon: Zap,
      color: "text-emerald-500",
      bgColor: "bg-emerald-500/10",
    },
  ];

  // ==================== RENDER ====================

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <BarChart3 className="h-6 w-6" /> API Usage
        </h1>
        <p className="text-muted-foreground mt-1">
          Monitor Partner API request volume, performance, and webhook delivery
        </p>
      </div>

      {/* KPI Cards */}
      {statsLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {kpis.map((kpi) => (
            <Card key={kpi.title}>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className={cn("p-1.5 rounded-md", kpi.bgColor)}>
                    <kpi.icon className={cn("h-4 w-4", kpi.color)} />
                  </div>
                  <span className="text-xs text-muted-foreground">{kpi.title}</span>
                </div>
                <div className="text-2xl font-bold">{kpi.value}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{kpi.subtitle}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Daily Requests Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Daily API Requests</CardTitle>
          <CardDescription>Request volume and errors over the last 30 days</CardDescription>
        </CardHeader>
        <CardContent>
          {usageLoading ? (
            <Skeleton className="h-[300px] w-full" />
          ) : usage?.daily_requests && usage.daily_requests.length > 0 ? (
            <ChartContainer config={chartConfig} className="h-[300px] w-full">
              <AreaChart data={usage.daily_requests}>
                <defs>
                  <linearGradient id="gradientRequests" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--color-count)" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="var(--color-count)" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="gradientErrors" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--color-errors)" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="var(--color-errors)" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                />
                <YAxis tick={{ fontSize: 12 }} tickLine={false} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="var(--color-count)"
                  strokeWidth={2}
                  fill="url(#gradientRequests)"
                  name="Requests"
                />
                <Area
                  type="monotone"
                  dataKey="errors"
                  stroke="var(--color-errors)"
                  strokeWidth={2}
                  fill="url(#gradientErrors)"
                  name="Errors"
                />
              </AreaChart>
            </ChartContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-muted-foreground">
              No usage data available yet
            </div>
          )}
        </CardContent>
      </Card>

      {/* Two-column: Top Endpoints + Per-Key Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Endpoints */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top Endpoints</CardTitle>
            <CardDescription>Most frequently called API endpoints</CardDescription>
          </CardHeader>
          <CardContent>
            {usageLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
              </div>
            ) : usage?.top_endpoints && usage.top_endpoints.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Method</TableHead>
                    <TableHead>Endpoint</TableHead>
                    <TableHead className="text-right">Requests</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usage.top_endpoints.map((ep, i) => (
                    <TableRow key={i}>
                      <TableCell>{getMethodBadge(ep.method)}</TableCell>
                      <TableCell className="font-mono text-xs max-w-[180px] truncate">{ep.path}</TableCell>
                      <TableCell className="text-right font-medium">{formatNumber(ep.count)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground text-sm">No endpoint data yet</div>
            )}
          </CardContent>
        </Card>

        {/* Per-Key Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Usage by API Key</CardTitle>
            <CardDescription>Request volume per key</CardDescription>
          </CardHeader>
          <CardContent>
            {usageLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
              </div>
            ) : usage?.per_key && usage.per_key.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Key</TableHead>
                    <TableHead>Prefix</TableHead>
                    <TableHead className="text-right">Requests</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usage.per_key.map((k) => (
                    <TableRow key={k.key_id}>
                      <TableCell className="font-medium max-w-[120px] truncate">{k.key_name}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{k.key_prefix}</TableCell>
                      <TableCell className="text-right font-medium">{formatNumber(k.request_count)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground text-sm">No per-key data yet</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Webhook Events */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent Webhook Events</CardTitle>
          <CardDescription>Latest webhook delivery attempts</CardDescription>
        </CardHeader>
        <CardContent>
          {eventsLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : webhookEvents?.events && webhookEvents.events.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>URL</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Attempts</TableHead>
                  <TableHead>Response</TableHead>
                  <TableHead>Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {webhookEvents.events.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell className="font-mono text-xs max-w-[180px] truncate">
                      {event.webhook_url}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-xs">{event.event_type}</Badge>
                    </TableCell>
                    <TableCell>{getEventStatusBadge(event.status)}</TableCell>
                    <TableCell className="text-sm">
                      {event.attempts}/{event.max_attempts}
                    </TableCell>
                    <TableCell>
                      {event.last_response_code ? (
                        <Badge
                          variant={event.last_response_code < 400 ? "secondary" : "destructive"}
                          className="text-xs border-0"
                        >
                          {event.last_response_code}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDateTime(event.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground text-sm">No webhook events yet</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
